import { cacheImages } from 'api/cacheImages';
import { S3_IMAGE_BUCKET_LINK } from 'constants/imageUpload';
import { IMAGE_CREATED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { store } from 'modules/Editor/store';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { useToast } from 'modules/common/components/Toast';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { ImageLibResponse } from 'types/imageLibsTypes';
import { generateDefaultData as generateImageData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { TOAST_TIME } from './PhotoWidgetMenu.config';
import { AuthorCreditProps } from './PhotoWidgetMenu.types';

interface PhotoMenuContextValue {
  onItemClick: (image: ImageLibResponse['images'][number], index: number) => Promise<NewWidget>;
}

const PhotoMenuContext = createContext<PhotoMenuContextValue>({
  onItemClick: () => new Promise(() => {}),
});

export interface PhotoMenuProviderProps {
  children: ReactNode;
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  setImageMeta: Dispatch<SetStateAction<AuthorCreditProps>>;
}

export const PhotoMenuProvider = memo(
  ({ children, onItemClick: onItemClickProp, setImageMeta }: PhotoMenuProviderProps) => {
    // A flag indicating if image caching is in progress.
    const isCachingImage = useRef(false);

    const toast = useToast();

    // Since the functions from the props maybe changed, so anything depends on those would be re-render.
    // In this Photo panel, every thumbnail would depend on this, so entired list would re-render un-necessarily.
    // Using a reference to store the functions and pass the reference to children could avoid the re-render.
    const toastRef = useRef(toast);
    const onItemClickRef = useRef(onItemClickProp);
    const setImageMetaRef = useRef(setImageMeta);
    toastRef.current = toast;
    onItemClickRef.current = onItemClickProp;
    setImageMetaRef.current = setImageMeta;

    const onItemClick = useCallback(
      (image: ImageLibResponse['images'][number]) =>
        new Promise<NewWidget>((resolve, reject) => {
          const activePageId = selectActivePage(store.getState());

          // Skip the image caching if currently working on a image caching
          if (isCachingImage.current) return;

          // Only cache the image if the image is not cached yet, which means a webformatURL is provided
          isCachingImage.current = !!image.webformatURL;

          // Display the image credit
          setImageMetaRef.current({
            author: image.author,
            provider: image.source,
          });

          if (!image.webformatURL) {
            const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
            const widget = generateImageData(
              image.url, // image url
              { width: image.imageWidth, height: image.imageHeight }, //image size
              { width: infographWidth, height: infographHeight }, // infograph size
              top,
              left,
            );

            onItemClickRef.current(widget);
            resolve(widget);

            Mixpanel.track(IMAGE_CREATED.eventName, {
              from: IMAGE_CREATED.from.Search,
            });

            // Skip the image caching logic if the image is not need to be cached
            return;
          }

          // Cache the image, the url from the pixabay is valid for 24 hours only
          cacheImages(activePageId, image.webformatURL)
            .then(({ url }) => {
              const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
              const widget = generateImageData(
                `${S3_IMAGE_BUCKET_LINK}/${url}`, // image url
                { width: image.imageWidth, height: image.imageHeight }, //image size
                { width: infographWidth, height: infographHeight }, // infograph size
                top,
                left,
              );

              onItemClickRef.current(widget);
              resolve(widget);

              Mixpanel.track(IMAGE_CREATED.eventName, {
                from: IMAGE_CREATED.from.Search,
              });
            })
            .catch((err) => {
              const errorMessage = `Cannot cache the image ${image.webformatURL}`;

              // print the error meesag to the console in case the user got error and asking dev sup.
              console.error(errorMessage);

              toastRef.current({
                title: 'Image failed to load.',
                status: 'error',
                duration: TOAST_TIME,
              });

              reject(new Error(errorMessage));
            })
            .finally(() => {
              isCachingImage.current = false;
            });
        }),
      [],
    );

    const value = useMemo(() => {
      return {
        onItemClick,
      };
    }, [onItemClick]);

    return <PhotoMenuContext.Provider value={value}>{children}</PhotoMenuContext.Provider>;
  },
);
PhotoMenuProvider.displayName = 'PhotoMenuProvider';

export const usePhotoMenu = () => useContext(PhotoMenuContext);
